// SecureEmbed InnovateFlow — Personal Preferences (Plan §6)
// Reads the server-resolved 4-layer preferences (platform → org-type pack →
// org policy w/ locked keys → personal) and lets the user edit personal keys.
// Locked keys render with a lock chip and are disabled — the user sees the lock
// rather than discovering it by a failed write.
import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { LucideIcon } from 'lucide-react'
import { Lock, Info, Loader2, User, Building2, Layers, ShieldCheck } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useActiveOrg } from '../../hooks/useActiveOrg'
import { getKeyConfig, getMyPreferences, setMyPreference, getOrgPreferencePolicy, setOrgPreferencePolicy } from '../../services/api'
import PageHeader from '../../components/ui/PageHeader'
import ContextualTip from '../../components/ui/ContextualTip'
import { NextStepBanner, useSetSpine } from '../../components/spine'
import WidestViewPolicy from '../../components/organisation/WidestViewPolicy'
import LinkedAggregatorPanel from '../../components/organisation/LinkedAggregatorPanel'
import OrgVocabularyPanel from '../../components/organisation/OrgVocabularyPanel'
import EncryptionKeyPolicyPanel from '../../components/organisation/EncryptionKeyPolicyPanel'
import MemberDefaultsPanel from '../../components/organisation/MemberDefaultsPanel'
import type { OrgPolicyResponse, OrgPolicyDraft, UnlinkAggregatorPayload } from '../../components/organisation/types'

type Opt = { value: string; label: string }
type Field =
  | { key: string; label: string; type: 'toggle' }
  | { key: string; label: string; type: 'select'; options: Opt[] }

const GROUPS: { title: string; hint?: string; fields: Field[] }[] = [
  {
    title: 'Appearance',
    fields: [
      { key: 'theme', label: 'Theme', type: 'select', options: [
        { value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }, { value: 'auto', label: 'Auto' }] },
      { key: 'density', label: 'Density', type: 'select', options: [
        { value: 'comfortable', label: 'Comfortable' }, { value: 'compact', label: 'Compact' }, { value: 'dense', label: 'Dense' }] },
    ],
  },
  {
    title: 'Locale',
    fields: [
      { key: 'timezone', label: 'Timezone', type: 'select', options: [
        { value: 'Australia/Sydney', label: 'Australia/Sydney (AEDT)' },
        { value: 'Australia/Perth', label: 'Australia/Perth (AWST)' },
        { value: 'UTC', label: 'UTC' }] },
      { key: 'locale', label: 'Language', type: 'select', options: [
        { value: 'en-AU', label: 'English (Australia)' }, { value: 'en-GB', label: 'English (UK)' }, { value: 'en-US', label: 'English (US)' }] },
    ],
  },
  {
    title: 'Notifications',
    fields: [
      { key: 'notifications_email', label: 'Email notifications', type: 'toggle' },
      { key: 'notifications_inapp', label: 'In-app notifications', type: 'toggle' },
    ],
  },
  {
    title: 'AI defaults',
    hint: 'Model options are capped by your plan tier.',
    fields: [
      { key: 'ai_default_model', label: 'Default model', type: 'select', options: [
        { value: 'sonnet', label: 'Sonnet' }, { value: 'opus', label: 'Opus' }] },
      { key: 'analysis_depth', label: 'Analysis depth', type: 'select', options: [
        { value: 'standard', label: 'Standard' }, { value: 'deep', label: 'Deep' }] },
      { key: 'citation_style', label: 'Citation style', type: 'select', options: [
        { value: 'apa', label: 'APA' }, { value: 'ieee', label: 'IEEE' }, { value: 'harvard', label: 'Harvard' }] },
    ],
  },
  {
    title: 'Privacy',
    fields: [
      { key: 'disclosure_receipts', label: 'Disclosure receipts', type: 'toggle' },
    ],
  },
]

const SOURCE_LABEL: Record<string, string> = {
  platform: 'Platform default', org_type: 'Organisation type',
  org_policy: 'Organisation policy', org_policy_locked: 'Locked by organisation',
  personal: 'Your choice',
}

/** The anchor figure C4's "Review the lock ↓" scrolls to. */
const FOCUS_CARD_ID = 'org-focus-group'

/** "Meridian Ventures' workspace", not "Meridian Ventures's workspace". */
/**
 * The organisation as a readable workspace handle — "meridian-ventures", never
 * the raw id.
 *
 * Figure C4's footer hint reads `GET /preferences?workspace=org:meridian`. The
 * live page printed `GET /preferences/org/895e4448-6011-70a2-6bc9-c75ddc6e1bf5`
 * — the single raw UUID on any of the five captures the round-3 review looked
 * at, in italic body copy, on a screen whose whole subject is who can see what.
 * A UUID tells the reader nothing the sentence is trying to say; the sentence's
 * point is that the workspace is NAMED in the request, and a name is what
 * should be shown.
 *
 * Bracketed suffixes go ("BYOK Test Tenant (dev fixture)" is a test org's
 * bookkeeping, not its name), then the first two words, so the handle
 * stays as short as the figure's.
 */
export function organisationSlug(name: string): string {
  const cleaned = (name || '').replace(/\([^)]*\)/g, ' ').trim()
  const words = cleaned
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
  return words.length ? words.join('-') : 'this-organisation'
}

function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}’` : `${name}’s`
}

// The three values preferences/handler.py's POLICY_KEY_VALUES allows for
// widest_view, in the words figure C4 prints for each.
const WIDEST_VIEW_LABEL: Record<string, string> = {
  entity: 'Stop at one Entity',
  portfolio: 'Stop at the Portfolio',
  no_limit: 'No limit',
}

// ---------------------------------------------------------------------------
// Precedence ladder (Fig68) — visualises the SAME four-layer resolution the
// backend performs in preferences/handler.py `_resolve()`: platform default →
// organisation-type pack → organisation policy (± locked) → personal choice
// (highest, unless locked). Built entirely from the real `sources`/
// `locked_keys` already returned by getMyPreferences — no extra calls, and no
// invented "aggregator" layer: this account's preference system genuinely has
// these four layers, not the encryption-key precedence shown elsewhere.
type LayerId = 'personal' | 'org_policy' | 'org_type' | 'platform'
type LayerState = 'active' | 'active-locked' | 'blocked' | 'not-set' | 'not-reached'

const LAYER_ORDER: LayerId[] = ['personal', 'org_policy', 'org_type', 'platform']

const LAYER_META: Record<LayerId, { title: string; icon: LucideIcon; blurb: string }> = {
  personal: {
    title: 'Your personal choice',
    icon: User,
    blurb: 'The value you’ve set yourself. Highest precedence — unless your organisation has locked this setting.',
  },
  org_policy: {
    title: 'Organisation policy',
    icon: Building2,
    blurb: 'Set by an admin of your organisation. Can be locked to override every member’s personal choice.',
  },
  org_type: {
    title: 'Organisation-type default',
    icon: Layers,
    blurb: 'A starting default for your kind of organisation, used only when no policy or personal value applies.',
  },
  platform: {
    title: 'Platform default',
    icon: ShieldCheck,
    blurb: 'InnovateFlow’s built-in default — always available as the last resort so the setting is never left undefined.',
  },
}

// Only the keys the org-policy/org-type layers can ever govern (see
// POLICY_MANAGEABLE_KEYS in preferences/handler.py) make an interesting
// example — personal-taste keys like theme/density always resolve as either
// 'platform' or 'personal', so the ladder would never show layers 2 or 3.
const GOVERNABLE_KEYS = [
  'notifications_email', 'notifications_inapp', 'ai_default_model',
  'analysis_depth', 'citation_style', 'disclosure_receipts',
]

const FIELD_LABEL: Record<string, string> = Object.fromEntries(
  GROUPS.flatMap((g) => g.fields).map((f) => [f.key, f.label])
)

interface LadderLayer {
  id: LayerId
  title: string
  icon: LucideIcon
  blurb: string
  state: LayerState
}

/** Real per-layer state for one setting, derived from its resolved `source`. */
function buildLadder(source: string | undefined, isLocked: boolean): LadderLayer[] {
  const winner: LayerId = source === 'org_policy_locked' ? 'org_policy' : ((source as LayerId) || 'platform')
  const winnerIdx = LAYER_ORDER.indexOf(winner)
  return LAYER_ORDER.map((id, i) => {
    let state: LayerState
    if (i === winnerIdx) state = isLocked && id === 'org_policy' ? 'active-locked' : 'active'
    else if (i < winnerIdx) state = i === 0 && isLocked ? 'blocked' : 'not-set'
    else state = 'not-reached'
    return { id, ...LAYER_META[id], state }
  })
}

/** The honest, real reason the active layer won — no generic "it's better". */
function layerWhyText(layer: LadderLayer): string {
  switch (layer.id) {
    case 'personal':
      return 'You’ve set a personal value and your organisation hasn’t locked this setting, so your choice wins.'
    case 'org_policy':
      return layer.state === 'active-locked'
        ? 'Your organisation has locked this setting, so it overrides your personal choice and outranks the layers below it.'
        : 'Your organisation has a policy value for this setting and you haven’t set a personal override, so the policy applies.'
    case 'org_type':
      return 'Your organisation type provides a default for this setting, and neither an organisation policy nor a personal choice overrides it.'
    case 'platform':
    default:
      return 'No organisation policy or personal choice is set for this, so InnovateFlow’s platform default applies.'
  }
}

/** The connector line between two adjacent layers, mirroring Fig68's arrows. */
function layerConnectorText(layers: LadderLayer[], i: number): string {
  const from = layers[i]
  const to = layers[i + 1]
  const isLast = i === layers.length - 2
  if (from.state === 'blocked') return `overridden by ${to.title.toLowerCase()}`
  if (from.state === 'active' || from.state === 'active-locked') {
    return isLast ? `finally falls back to ${to.title.toLowerCase()}` : `would fall through to ${to.title.toLowerCase()}`
  }
  return `falls through to ${to.title.toLowerCase()}`
}

const LAYER_BADGE: Partial<Record<LayerState, { label: string; className: string }>> = {
  active: { label: 'In effect', className: 'bg-emerald-100 text-emerald-700' },
  'active-locked': { label: 'Locked · in effect', className: 'bg-amber-100 text-amber-700' },
  blocked: { label: 'Set, not in effect', className: 'bg-gray-100 text-gray-500' },
  'not-set': { label: 'Not set', className: 'bg-gray-100 text-gray-500' },
  'not-reached': { label: 'Not reached', className: 'bg-gray-100 text-gray-500' },
}

/** Picks the most illustrative real example: a locked key first (most
 * informative), then a key an org policy has actually customised, then an
 * org-type default, then any governable key so the ladder always has
 * something real to show. */
function pickDefaultExampleKey(sources: Record<string, string>, lockedKeys: string[]): string {
  const locked = GOVERNABLE_KEYS.find((k) => lockedKeys.includes(k))
  if (locked) return locked
  const orgPolicyKey = GOVERNABLE_KEYS.find((k) => sources[k] === 'org_policy')
  if (orgPolicyKey) return orgPolicyKey
  const orgTypeKey = GOVERNABLE_KEYS.find((k) => sources[k] === 'org_type')
  if (orgTypeKey) return orgTypeKey
  const personalKey = GOVERNABLE_KEYS.find((k) => sources[k] === 'personal')
  if (personalKey) return personalKey
  return GOVERNABLE_KEYS.find((k) => k in sources) ?? GOVERNABLE_KEYS[0]
}

export default function Preferences() {
  const { isAuthenticated } = useAuth()
  const qc = useQueryClient()
  // One component serves /account/preferences (a member's own page) and
  // /org/preferences (figure C4, the organisation's policy). They are different
  // screens and the figure shows only the second one's content on that route.
  const isOrgScreen = useLocation().pathname.startsWith('/org/')
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [selectedExampleKey, setSelectedExampleKey] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['my-preferences'], queryFn: getMyPreferences, enabled: isAuthenticated,
  })

  const mut = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) => setMyPreference(key, value),
    onMutate: ({ key }) => { setSavingKey(key); setSaveError(null) },
    onSuccess: (fresh) => qc.setQueryData(['my-preferences'], fresh),
    // BUG-121: surface a real failure instead of swallowing it — the toggle
    // silently no-op'd before, so the user thought it saved.
    onError: () => setSaveError('Could not save that preference. Please try again.'),
    onSettled: () => setSavingKey(null),
  })

  // ---------------------------------------------------------------------
  // Organisation policy (Story #5648 lane B3, contract row C4 / Figure 15).
  // Resolution is the shared useActiveOrg hook — one screen must
  // not govern a different organisation from the others.
  // ---------------------------------------------------------------------
  const { orgId, orgName: membershipOrgName, isOrgAdmin } = useActiveOrg()
  const orgName = membershipOrgName || 'your organisation'
  const orgPolicyEnabled = Boolean(isOrgAdmin && orgId)

  const orgPolicyQuery = useQuery({
    queryKey: ['org-preference-policy', orgId],
    // Bridge 1 (components/organisation/types.ts): api.ts:618's return type
    // does not yet carry linked_aggregator — narrowed here, once, at the
    // query boundary. A7 follow-up: publish the two org-policy keys in the
    // shared preferences types (Story #5648 task #5742).
    queryFn: () => getOrgPreferencePolicy(orgId as string) as Promise<OrgPolicyResponse>,
    enabled: orgPolicyEnabled,
  })

  const [orgDraft, setOrgDraft] = useState<OrgPolicyDraft | null>(null)
  const [orgDirty, setOrgDirty] = useState(false)
  const [orgSaveError, setOrgSaveError] = useState<string | null>(null)
  // Kept as a write-only flag: nothing renders it any more. The saved-state
  // indicators now answer "is the draft the stored policy?" (orgDirty), which
  // is true on a fresh load too — a "saved" pill that only appears in the
  // seconds after a click tells an admin nothing when they arrive.
  const [, setOrgSaveOk] = useState(false)
  const [unlinkError, setUnlinkError] = useState<string | null>(null)

  useEffect(() => {
    if (orgPolicyQuery.data && !orgDirty) {
      setOrgDraft({
        org_prefs: { ...orgPolicyQuery.data.org_prefs },
        locked_keys: [...orgPolicyQuery.data.locked_keys],
      })
    }
  }, [orgPolicyQuery.data, orgDirty])

  const orgPolicyMut = useMutation({
    mutationFn: () => setOrgPreferencePolicy(orgId as string, orgDraft as OrgPolicyDraft),
    onMutate: () => { setOrgSaveError(null); setOrgSaveOk(false) },
    onSuccess: () => {
      setOrgDirty(false)
      setOrgSaveOk(true)
      qc.invalidateQueries({ queryKey: ['org-preference-policy', orgId] })
      qc.invalidateQueries({ queryKey: ['my-preferences'] })
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setOrgSaveError(msg || 'Could not save the organisation policy. Please try again.')
    },
  })

  const unlinkMut = useMutation({
    // Bridge 2 (components/organisation/types.ts): setOrgPreferencePolicy's
    // parameter type does not yet accept linked_aggregator — cast once, with
    // the same A7-follow-up note as bridge 1 above.
    mutationFn: () => setOrgPreferencePolicy(
      orgId as string,
      { linked_aggregator: null } as unknown as { org_prefs?: Record<string, unknown>; locked_keys?: string[] } & UnlinkAggregatorPayload,
    ),
    onMutate: () => setUnlinkError(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-preference-policy', orgId] }),
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setUnlinkError(msg || 'Could not end the link. Please try again.')
    },
  })

  const linkedAggregator = orgPolicyQuery.data?.linked_aggregator ?? null

  // The organisation's real key custody, for C4's "Encryption key policy" card.
  const keyConfigQuery = useQuery({
    queryKey: ['org-key-config', orgId],
    queryFn: () => getKeyConfig(orgId as string),
    enabled: orgPolicyEnabled,
  })

  const updateOrgPref = (key: string, value: unknown) => {
    setOrgDraft((prev) => {
      const base = prev || { org_prefs: {}, locked_keys: [] }
      return { ...base, org_prefs: { ...base.org_prefs, [key]: value } }
    })
    setOrgDirty(true)
    setOrgSaveOk(false)
  }
  const updateOrgLock = (key: string, locked: boolean) => {
    setOrgDraft((prev) => {
      const base = prev || { org_prefs: {}, locked_keys: [] }
      const locked_keys = locked
        ? Array.from(new Set([...base.locked_keys, key]))
        : base.locked_keys.filter((k) => k !== key)
      return { ...base, locked_keys }
    })
    setOrgDirty(true)
    setOrgSaveOk(false)
  }

  // A control screen, not a level (figure C4 draws no rail here): an empty
  // `levels` gives the crumb with no notches. Declared for the org route only;
  // on the personal route the spec is null and the shell shows nothing, as
  // before. "Administration" is plain because it is not a place you can go —
  // the figure shows it unbolded between two bold segments.
  useSetSpine(isOrgScreen ? {
    level: 'portfolio',
    levels: [],
    youreAt: [orgName],
    crumb: [orgName, { label: 'Administration', plain: true }, 'Preferences'],
  } : null)

  if (!isAuthenticated) return null
  if (isLoading || !data) {
    return <div className="flex items-center gap-2 text-gray-500 p-8"><Loader2 className="animate-spin" size={18} /> Loading preferences…</div>
  }

  const prefs = data.preferences || {}
  const lockedKeys = data.locked_keys || []
  const locked = new Set(lockedKeys)
  const sources = data.sources || {}

  const update = (key: string, value: unknown) => { if (!locked.has(key)) mut.mutate({ key, value }) }

  const availableExampleKeys = GOVERNABLE_KEYS.filter((k) => k in sources)
  const exampleKey = (selectedExampleKey && availableExampleKeys.includes(selectedExampleKey))
    ? selectedExampleKey
    : pickDefaultExampleKey(sources, lockedKeys)
  const ladder = buildLadder(sources[exampleKey], locked.has(exampleKey))

  // ---------------------------------------------------------------------
  // C4 — /org/preferences. The ORGANISATION policy screen (figure C4), which
  // is a different screen from /account/preferences even though one component
  // serves both routes. It shows the organisation's Focus group, its link, its
  // vocabulary, its key policy and its member defaults — and NOT the personal
  // Appearance / Locale / Notifications / AI-defaults groups, which belong to a
  // member's own page and which the figure does not show here.
  //
  // A control screen, not a level: it declares its crumb so the shell renders
  // one line instead of the old shell's two, and rail:false because zoom is a
  // statement about where you are in the WORK, and a policy page is not
  // anywhere in the work.
  // ---------------------------------------------------------------------
  if (isOrgScreen) {
    if (!orgPolicyEnabled) {
      return (
        <div className="max-w-3xl">
          <PageHeader
            title="Preferences"
            description="Defaults and locks for every member of this organisation."
          />
          <p className="text-sm text-gray-600">
            {orgId
              ? 'Only an owner or admin of this organisation can set its policy.'
              : 'You are not an administrator of any organisation, so there is no policy to set here.'}
          </p>
        </div>
      )
    }

    const draft = orgDraft ?? { org_prefs: {}, locked_keys: [] }
    const widestValue =
      (draft.org_prefs.widest_view as 'entity' | 'portfolio' | 'no_limit') || 'no_limit'
    const widestLocked = draft.locked_keys.includes('widest_view')

    return (
      <div>
        <PageHeader
          title="Preferences"
          helpKey="account-preferences"
          description={`Defaults and locks for every member of ${orgName}. An unlocked value is a `
            + 'default a member may change; a locked value overrides their personal choice '
            + 'and says so on their page.'}
          /* The saved-state indicator, in the figure's own slot: `ph-actions`
             holds `<span class="pill pill-ok">● Policy saved</span>` and then
             the Save button, right-aligned together. Round 3 passed it as
             PageHeader's `status`, which renders beside the TITLE — so the pill
             sat at the far left of the header, inches from the button whose
             state it describes, and the C4 review found it three rounds
             running. It rides with the button now.

             It is not "a save just succeeded" — it is "what you are looking at
             IS the policy", which is the question an admin actually has. So it
             shows whenever the draft matches what is stored, including on a
             fresh load, and disappears the moment an edit makes the two differ. */
          primaryAction={
            <div className="flex items-center gap-2">
              {!orgDirty && (
                <span className="inline-flex shrink-0 items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                  ● Policy saved
                </span>
              )}
              <button
                type="button"
                disabled={!orgDirty || orgPolicyMut.isPending}
                onClick={() => orgPolicyMut.mutate()}
                /* The figure draws a saturated blue primary here, and it stays
                   blue: round 2 rendered flat grey with nothing to save, which
                   reads as a control that is broken rather than one with
                   nothing to do. Blue always, dimmed and not-allowed when the
                   draft already IS the stored policy — the pill beside it says
                   why. */
                className={`px-4 py-2 rounded-lg text-sm font-bold text-white ${
                  orgDirty && !orgPolicyMut.isPending
                    ? 'bg-iflow-600 hover:bg-iflow-700'
                    : 'bg-iflow-600/45 cursor-not-allowed'}`}
              >
                {orgPolicyMut.isPending ? <Loader2 className="animate-spin inline" size={14} /> : 'Save policy'}
              </button>
            </div>
          }
        />

        {/* The step waiting at Organisation, from the live lock state — never a
            constant. A locked widest view is a decision somebody made that
            outranks every member, so it is the thing to confirm or undo. */}
        {widestLocked ? (
          <NextStepBanner
            at="ORGANISATION"
            verb="CONFIRM LOCK"
            headline={`Widest view is locked at ${WIDEST_VIEW_LABEL[widestValue]} for this organisation's workspaces. Keep the lock, or unlock it.`}
            rule="from your policy resolver · four layers, most specific wins, a lock re-asserts the organisation layer"
            cta={{ label: 'Review the lock ↓', onClick: () => {
              // Scrolls to the Focus card by its own id. Deliberately NOT by the
              // component's data-part: that attribute belongs to the component
              // that renders it, and naming it from here makes this page look
              // like the thing that emits it.
              document.getElementById(FOCUS_CARD_ID)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            } }}
          />
        ) : (
          <NextStepBanner
            at="ORGANISATION"
            verb=""
            none
            headline={`Widest view is not locked, so every member may set their own — the organisation's value (${WIDEST_VIEW_LABEL[widestValue]}) is a default, not a rule.`}
            rule="from your policy resolver · four layers, most specific wins, a lock re-asserts the organisation layer"
          />
        )}

        {orgSaveError && (
          <div role="alert" className="mb-4 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">
            {orgSaveError}
          </div>
        )}

        {/* `grid; 2fr 1fr; gap 16px` with NO align-items — read straight out of
            the figure's own kit.css (.grid + .g-2-1), so both columns stretch to
            the row's height. Round 3's `items-start` left the Focus card
            stopping ~380px short and the page ending in a band of empty page
            background; round 4 removed it and the card reached the line but
            carried ~430px of white below its footer hint. The stretch is right —
            what was wrong is that nothing inside the card used the height. The
            ladder does now (see WidestViewPolicy), so the card is filled by its
            content the way the figure's is. */}
        <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
          {/* LEFT — the Focus group */}
          <div id={FOCUS_CARD_ID} className="light-card flex flex-col p-4 lg:p-6">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <h2 className="text-[11px] font-bold uppercase tracking-wider text-iflow-700 flex items-center gap-2">
                Focus
                <span className="rounded-full bg-iflow-100 px-2 py-0.5 text-[11px] font-semibold text-iflow-700 normal-case tracking-normal">
                  New group
                </span>
              </h2>
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500">
                Resolved by precedence
              </span>
            </div>

            <p className="text-xs text-gray-600 leading-relaxed mb-3">
              <strong>
                {"Applies in this organisation's workspace and in " + possessive(orgName)
                  + ' workspace. It never changes what anyone sees of their own inventions.'}
              </strong>{' '}
              {"It never binds in a member's Personal workspace, where the floor is My inventions. "}
              {'The rail is '}<strong>never numbered</strong>
              {'; the floor is always Invention — nobody can be locked out of their own pipeline.'}
            </p>

            <WidestViewPolicy
              value={widestValue}
              locked={widestLocked}
              source={data?.sources?.widest_view}
              orgName={orgName}
              onChange={(next) => updateOrgPref('widest_view', next)}
              onLockChange={(next) => updateOrgLock('widest_view', next)}
              /* Bug #5846 — figure C4's ladder. The two starting values mirror
                 preferences/handler.py exactly: PLATFORM_DEFAULTS['widest_view']
                 is 'no_limit', and the corporate_industry pack sets 'no_limit'
                 too. They are stated here rather than fetched because the
                 /preferences route publishes only the RESOLVED value, not the
                 per-layer ones; `source` is what says which layer won, and that
                 IS live. */
              platformValue="no_limit"
              orgTypeValue="no_limit"
              policySet={'widest_view' in (orgPolicyQuery.data?.org_prefs ?? {})}
              policyUpdatedAt={orgPolicyQuery.data?.prefs_updated_at ?? null}
              policyUpdatedByName={orgPolicyQuery.data?.prefs_updated_by_name ?? null}
            />

            {/* The figure's one divider, at the figure's own offsets:
                `.divider{height:1px;margin:14px 0}` under a last rung whose
                `.keylayer{margin-bottom:8px}` collapses into it — 14px above
                the line, 14px below it. Round 8 measured 259px here because
                the ladder was still growing to fill the card and pushing this
                block ahead of it; the ladder no longer grows, and these are the
                numbers that put the divider back under rung 4. */}
            <div className="border-t border-gray-100 mt-[14px] pt-[14px]">
              <p className="text-[11.5px] italic leading-relaxed text-gray-500">
                This page reads its own values through{' '}
                <span className="font-mono not-italic">
                  GET /preferences/org/{organisationSlug(orgName)}
                </span> — the
                workspace is always named in the request, never inferred from a membership list.
              </p>
            </div>
          </div>

          {/* RIGHT — the existing groups */}
          <div className="flex flex-col gap-4">
            <LinkedAggregatorPanel
              link={linkedAggregator}
              onUnlink={() => unlinkMut.mutate()}
              unlinking={unlinkMut.isPending}
              error={unlinkError}
            />
            <OrgVocabularyPanel />
            <EncryptionKeyPolicyPanel
              custodyMode={keyConfigQuery.data?.custody_mode ?? null}
              loaded={!keyConfigQuery.isPending}
              error={keyConfigQuery.isError}
            />
            <MemberDefaultsPanel
              orgPrefs={draft.org_prefs}
              lockedKeys={draft.locked_keys}
              resolved={prefs}
              /* Same rule as the header pill: "this card is what is stored",
                 not "a save just happened". */
              saved={!orgDirty}
              onChange={updateOrgPref}
              onLockChange={updateOrgLock}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Preferences"
        description="Personalise the interface — appearance, locale, notifications and AI defaults. Settings follow you across devices."
        helpKey="account-preferences"
      />

      <ContextualTip id="prefs-layers" tone="neutral">
        Values resolve through four layers — platform defaults, your organisation type, your
        organisation’s policy, then your personal choices. A{' '}
        <Lock size={12} className="inline mx-0.5 -mt-0.5 text-amber-600" /> locked setting is set by
        your organisation and can’t be changed here.
      </ContextualTip>

      {/* Precedence ladder (Fig68) — real, live per-layer resolution for
          whichever governable setting the user picks, driven entirely by this
          account's actual sources/locked_keys. */}
      {availableExampleKeys.length > 0 && (
        <div className="light-card p-4 lg:p-6 mt-5">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <h2 className="text-base font-semibold text-iflow-900">Preference precedence</h2>
            <span className="text-[11px] font-medium text-iflow-700 bg-iflow-50 px-2 py-0.5 rounded-full">Resolved automatically</span>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Every governed setting below resolves through the same four layers, most specific first. Pick one to see
            exactly which layer is active for it, and why.
          </p>
          <select
            aria-label="Show precedence for setting"
            value={exampleKey}
            onChange={(e) => setSelectedExampleKey(e.target.value)}
            className="light-input text-sm rounded-lg px-3 py-1.5 mb-4"
          >
            {availableExampleKeys.map((k) => <option key={k} value={k}>{FIELD_LABEL[k] || k}</option>)}
          </select>

          <div>
            {ladder.map((layer, i) => {
              const badge = LAYER_BADGE[layer.state]
              const isActive = layer.state === 'active' || layer.state === 'active-locked'
              return (
                <div key={layer.id}>
                  <div
                    data-testid={`precedence-layer-${layer.id}`}
                    data-state={layer.state}
                    className={`rounded-lg border p-3 flex items-start gap-3 ${
                      isActive ? 'border-iflow-400 bg-iflow-50/70 ring-1 ring-iflow-200' : 'border-gray-200'
                    }`}
                  >
                    <span className="mt-0.5 w-4 shrink-0 text-xs font-semibold text-gray-400">{i + 1}</span>
                    <layer.icon size={16} className={`mt-0.5 shrink-0 ${isActive ? 'text-iflow-600' : 'text-gray-400'}`} aria-hidden />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">{layer.title}</span>
                        {badge && (
                          <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${badge.className}`}>
                            {layer.state === 'active-locked' && <Lock size={9} />}
                            {badge.label}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{layer.blurb}</p>
                      {isActive && (
                        <p className="text-xs text-iflow-800 bg-white/70 border border-iflow-100 rounded px-2 py-1 mt-2">
                          <strong>Why this layer wins:</strong> {layerWhyText(layer)}
                        </p>
                      )}
                    </div>
                  </div>
                  {i < ladder.length - 1 && (
                    <p className="text-center text-[11px] text-gray-400 py-1">↓ {layerConnectorText(ladder, i)}</p>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {saveError && (
        <div role="alert" className="mt-4 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">
          {saveError}
        </div>
      )}

      <div className="space-y-5 mt-5">
        {GROUPS.map((group) => (
          <div key={group.title} className="light-card p-4 lg:p-6">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-base font-semibold text-iflow-900">{group.title}</h2>
            </div>
            {group.hint && (
              <p className="text-xs text-gray-500 flex items-center gap-1 mb-3"><Info size={12} /> {group.hint}</p>
            )}
            <div className="divide-y divide-gray-100">
              {group.fields.map((f) => {
                const isLocked = locked.has(f.key)
                const val = prefs[f.key]
                return (
                  <div key={f.key} className="flex items-center justify-between py-3 gap-4">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-800 flex items-center gap-2">
                        {f.label}
                        {isLocked && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                            <Lock size={11} /> Locked
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-gray-500">{SOURCE_LABEL[sources[f.key]] || ''}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {savingKey === f.key && <Loader2 className="animate-spin text-iflow-500" size={14} />}
                      {f.type === 'toggle' ? (
                        <button
                          disabled={isLocked}
                          onClick={() => update(f.key, !val)}
                          className={`relative w-10 h-6 rounded-full transition-colors ${
                            val ? 'bg-iflow-500' : 'bg-gray-300'} ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                          aria-pressed={!!val}
                        >
                          <span className={`absolute top-0.5 ${val ? 'right-0.5' : 'left-0.5'} w-5 h-5 bg-white rounded-full shadow`} />
                        </button>
                      ) : (
                        <select
                          disabled={isLocked}
                          value={String(val ?? '')}
                          onChange={(e) => update(f.key, e.target.value)}
                          className={`light-input text-sm rounded-lg px-3 py-1.5 ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
